import {
  BillingPeriodOverviewCard,
  ChakraFlex,
  DateFormat,
  EmptySectionCard,
  SkeletonLoader,
} from '@equip.health/ui';
import isEmpty from 'lodash/isEmpty';

import { BILLING_PERIODS_TOP_LEVEL_ID } from '~/lib/constants';
import { OTHER_PAYOR } from '~/lib/constants/inquiry.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import {
  BillingPeriodAuthorizationStatusMapper,
  getBillingPeriodDateRange,
  isBillingPeriodActive,
} from '~/lib/util/billingPeriod.util';
import { formattedDateFromISO } from '~/lib/util/date.util';

interface BillingPeriodsListProps {
  billingPeriods: BillingPeriod[];
  isLoading: boolean;
}

const BillingPeriodsList = ({
  billingPeriods,
  isLoading,
}: BillingPeriodsListProps) => {
  const { setComponentVisibility } = useComponentPortalContext();
  const openBillingPeriodAccordion = (currentBillingPeriod: BillingPeriod) => {
    setComponentVisibility({
      component: PortalComponent.BILLING_PERIOD_ACCORDION,
      data: currentBillingPeriod,
      isExpanded: true,
      isVisible: true,
    });
  };

  const handleBillingPeriodClick = (billingPeriod: BillingPeriod) => {
    openBillingPeriodAccordion(billingPeriod);
  };

  if (isEmpty(billingPeriods)) {
    return (
      <SkeletonLoader id="billing-periods-list" isLoaded={!isLoading}>
        <EmptySectionCard
          caption="There are no billing periods associated with this patient"
          id={BILLING_PERIODS_TOP_LEVEL_ID}
        />
      </SkeletonLoader>
    );
  }

  return (
    <ChakraFlex
      flexDirection="column"
      gridGap="12px"
      id={`${BILLING_PERIODS_TOP_LEVEL_ID}__list`}
    >
      {billingPeriods?.map((billingPeriod) => {
        const {
          authorizationDetails,
          billingPeriodId,
          customPayorName,
          endDate,
          episodeStartDate,
          payorName,
          payorProgramName,
          startDate,
          treatmentProgramName,
        } = billingPeriod;
        const treatmentEpisode =
          treatmentProgramName &&
          `${treatmentProgramName} (${formattedDateFromISO(
            episodeStartDate,
            DateFormat.shortMonthDayYear,
          )})`;
        return (
          <BillingPeriodOverviewCard
            authorizations={authorizationDetails?.map((a) => {
              const duration =
                a?.approvedSessionUnits && a?.approvedSessions
                  ? `${a.approvedSessions} ${a.approvedSessionUnits}`
                  : null;
              return {
                authId: a.externalId,
                authNumber: a.authorizationNumber
                  ? `Auth #${a.authorizationNumber}`
                  : 'Auth',
                dateRange: getBillingPeriodDateRange(
                  a.startDate?.toString(),
                  a.endDate?.toString(),
                ),
                duration,
                status: BillingPeriodAuthorizationStatusMapper[a.status],
              };
            })}
            billingPeriodId={billingPeriodId}
            dateRange={getBillingPeriodDateRange(
              startDate?.toString(),
              endDate?.toString(),
            )}
            insurancePlan={
              payorName === OTHER_PAYOR ? customPayorName : payorName
            }
            isActive={isBillingPeriodActive(
              startDate?.toString(),
              endDate?.toString(),
            )}
            key={billingPeriodId}
            onClick={() => handleBillingPeriodClick(billingPeriod)}
            payor={payorProgramName}
            treatmentEpisode={treatmentEpisode}
          />
        );
      })}
    </ChakraFlex>
  );
};

export default BillingPeriodsList;
